import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getAdminArticles, getArticleActivity, reviveArticle, deleteArticle, duplicateArticle, bulkDeleteArticles, bulkUpdateArticles, cleanMaliciousScripts } from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import { Plus, Edit, Trash2, Newspaper, Loader2, Eye, Share2, CheckSquare, Square, Minus, Copy, ShieldAlert, FileText, PlayCircle, Activity, RotateCcw } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import AdminLayout from '@/components/AdminLayout';
import { ArticleActivityTrail } from '@/components/admin/ArticleActivityTrail';

const TABS = ['All', 'Check On Yo Peoples News Feed', 'Cincy Worldstar', 'Check Your Claims'];

const AdminArticles = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('All');
  const [selected, setSelected] = useState(new Set());
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [bulkAction, setBulkAction] = useState(null); // 'delete' | 'category' | 'status'
  const [bulkValue, setBulkValue] = useState('');
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [viewThreshold, setViewThreshold] = useState(5);
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [cleaningScripts, setCleaningScripts] = useState(false);
  const [activityFilter, setActivityFilter] = useState('all');
  const [activitySort, setActivitySort] = useState('needs-attention');
  const [reviveId, setReviveId] = useState(null);
  const [revivingId, setRevivingId] = useState(null);
  const [trailArticle, setTrailArticle] = useState(null);
  const [trailData, setTrailData] = useState(null);
  const [trailLoading, setTrailLoading] = useState(false);
  const [reviveNotice, setReviveNotice] = useState(() => {
    try {
      const stored = window.sessionStorage.getItem('admin-revive-notice');
      if (!stored) return null;
      window.sessionStorage.removeItem('admin-revive-notice');
      return { ...JSON.parse(stored), restored: true };
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (!isAuthenticated) { navigate('/admin'); return; }
    fetchArticles();
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (reviveNotice?.restored && reviveNotice.type === 'success') {
      const timer = window.setTimeout(() => {
        toast.success('Story revived and moved back to the top of feeds', { id: 'revive-success', duration: 8000 });
      }, 400);
      return () => window.clearTimeout(timer);
    }
  }, [reviveNotice]);

  // Clear selection when tab changes
  useEffect(() => { setSelected(new Set()); }, [activeTab]);

  const fetchArticles = async () => {
    try {
      const response = await getAdminArticles();
      setArticles(response.data);
    } catch (error) {
      toast.error('Failed to fetch stories');
    } finally {
      setLoading(false);
    }
  };

  const categoryFiltered = activeTab === 'All' ? articles : articles.filter(a => a.category === activeTab);
  const activityFiltered = activityFilter === 'all'
    ? categoryFiltered
    : categoryFiltered.filter((article) => article.activity?.status === activityFilter);
  const filtered = [...activityFiltered].sort((a, b) => {
    if (activitySort === 'recent-clicks') {
      return new Date(b.activity?.last_clicked_at || 0) - new Date(a.activity?.last_clicked_at || 0);
    }
    if (activitySort === 'longest-streak') {
      return (b.activity?.streak_days || 0) - (a.activity?.streak_days || 0);
    }
    if (activitySort === 'newest') {
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    }
    const statusWeight = { dead: 0, new: 1, active: 2, unpublished: 3 };
    const weightDifference = (statusWeight[a.activity?.status] ?? 4) - (statusWeight[b.activity?.status] ?? 4);
    if (weightDifference !== 0) return weightDifference;
    return (b.activity?.days_idle || 0) - (a.activity?.days_idle || 0);
  });
  const activityCounts = articles.reduce((counts, article) => {
    const key = article.activity?.status || 'new';
    counts[key] = (counts[key] || 0) + 1;
    return counts;
  }, {});

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(a => a.id)));
    }
  };

  const allSelected = filtered.length > 0 && selected.size === filtered.length;
  const someSelected = selected.size > 0 && selected.size < filtered.length;

  const selectByViewThreshold = () => {
    const threshold = Number(viewThreshold) || 0;
    const matches = filtered.filter(a => (a.views || 0) <= threshold);
    if (matches.length === 0) {
      toast.error(`No stories with ${threshold} views or less in this tab`);
      return;
    }
    setSelected(new Set(matches.map(a => a.id)));
    toast.success(`Selected ${matches.length} stories with ${threshold} views or less`);
  };

  // Single delete
  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await deleteArticle(deleteId);
      setArticles(prev => prev.filter(a => a.id !== deleteId));
      selected.delete(deleteId);
      setSelected(new Set(selected));
      toast.success('Story deleted');
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  const handleDuplicate = async (id) => {
    setDuplicatingId(id);
    try {
      const response = await duplicateArticle(id);
      setArticles(prev => [response.data, ...prev]);
      toast.success('Story duplicated with a new URL!');
    } catch (error) {
      toast.error('Failed to duplicate story');
    } finally {
      setDuplicatingId(null);
    }
  };

  const handleRevive = () => {
    if (!reviveId) return;
    const targetId = reviveId;
    const revivedTitle = articles.find((article) => article.id === targetId)?.title || 'Story';
    const successNotice = { type: 'success', id: targetId, title: revivedTitle };
    setRevivingId(targetId);
    setReviveNotice(successNotice);
    window.sessionStorage.setItem('admin-revive-notice', JSON.stringify(successNotice));
    setReviveId(null);
    window.setTimeout(() => {
      toast.success('Story revived and moved back to the top of feeds', { id: 'revive-success', duration: 8000 });
    }, 50);
    const reloadFallback = window.setTimeout(() => window.location.reload(), 1200);
    reviveArticle(targetId)
      .then(() => getAdminArticles())
      .then((refreshed) => {
        window.clearTimeout(reloadFallback);
        window.sessionStorage.removeItem('admin-revive-notice');
        setArticles(refreshed.data);
        setRevivingId(null);
      })
      .catch((error) => {
        window.clearTimeout(reloadFallback);
        window.sessionStorage.removeItem('admin-revive-notice');
        console.error('Revive story failed:', error);
        setReviveNotice({ type: 'error', title: error?.response?.data?.detail || 'The story could not be revived.' });
        setRevivingId(null);
        toast.error(error?.response?.data?.detail || 'Failed to revive story');
      });
  };

  const handleOpenTrail = async (article) => {
    setTrailArticle(article);
    setTrailData(null);
    setTrailLoading(true);
    try {
      const response = await getArticleActivity(article.id, 30);
      setTrailData(response.data);
    } catch (error) {
      toast.error('Failed to load click trail');
    } finally {
      setTrailLoading(false);
    }
  };

  const handleCleanMaliciousScripts = async () => {
    setCleaningScripts(true);
    try {
      const response = await cleanMaliciousScripts();
      const { cleaned_count } = response.data;
      if (cleaned_count > 0) {
        toast.success(`Removed malicious scripts from ${cleaned_count} ${cleaned_count === 1 ? 'story' : 'stories'}`);
      } else {
        toast.success('No malicious scripts found. All clear!');
      }
    } catch (error) {
      toast.error('Failed to run script cleanup');
    } finally {
      setCleaningScripts(false);
    }
  };

  // Bulk actions
  const handleBulkAction = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    setBulkProcessing(true);
    try {
      if (bulkAction === 'delete') {
        const response = await bulkDeleteArticles(ids);
        if (response.data?.success) {
          setArticles(prev => prev.filter(a => !selected.has(a.id)));
          toast.success(`${ids.length} stories deleted`);
        } else {
          toast.error('Delete failed');
        }
      } else if (bulkAction === 'category' && bulkValue) {
        await bulkUpdateArticles(ids, { category: bulkValue });
        setArticles(prev => prev.map(a => selected.has(a.id) ? { ...a, category: bulkValue } : a));
        toast.success(`${ids.length} stories moved to ${bulkValue}`);
      } else if (bulkAction === 'status' && bulkValue) {
        await bulkUpdateArticles(ids, { status: bulkValue });
        setArticles(prev => prev.map(a => selected.has(a.id) ? { ...a, status: bulkValue } : a));
        toast.success(`${ids.length} stories set to ${bulkValue}`);
      }
      setSelected(new Set());
    } catch (error) {
      console.error('Bulk action error:', error);
      toast.error('Bulk action failed: ' + (error?.response?.data?.detail || error.message));
    } finally {
      setBulkProcessing(false);
      setBulkAction(null);
      setBulkValue('');
    }
  };

  if (!isAuthenticated) return null;

  return (
    <AdminLayout>
      <div data-testid="admin-articles-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white">Stories</h2>
            <p className="text-gray-500 mt-1">{articles.length} total · {filtered.length} shown</p>
          </div>
          <Link
            to="/admin/articles/new"
            className="flex items-center gap-2 px-6 py-3 bg-yellow-500 text-white font-medium hover:bg-yellow-600 transition-colors"
            data-testid="new-article-btn"
          >
            <Plus size={18} />
            Add New Story
          </Link>
        </div>

        {reviveNotice && (
          <div className={`mb-4 flex items-center gap-3 border px-3 py-2 text-sm ${reviveNotice.type === 'error' ? 'border-yellow-500/40 bg-yellow-500/10 text-yellow-300' : 'border-green-500/40 bg-green-500/10 text-green-300'}`} role="status" data-testid={reviveNotice.type === 'error' ? 'revive-error-alert' : 'revive-success-alert'}>
            <RotateCcw size={15} className="shrink-0" />
            <span className="min-w-0 flex-1">{reviveNotice.type === 'error' ? reviveNotice.title : <><strong>Story revived.</strong> {reviveNotice.title} is back at the top of feeds with the same URL.</>}</span>
            <button type="button" onClick={() => setReviveNotice(null)} className="text-xs font-bold opacity-80 hover:text-white hover:opacity-100" data-testid="dismiss-revive-feedback-button">Dismiss</button>
          </div>
        )}

        <div className="mb-4 border border-[#222] bg-[#0a0a0a] p-3" data-testid="activity-controls">
          <div className="flex flex-wrap items-center gap-2">
            <Activity size={15} className="text-yellow-500" />
            <span className="mr-1 text-xs font-semibold text-gray-300">Post trail</span>
            <Select value={activityFilter} onValueChange={setActivityFilter}>
              <SelectTrigger className="h-8 w-36 border-[#333] bg-black text-xs text-gray-300" data-testid="activity-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-[#222] bg-[#111]">
                <SelectItem value="all">All activity</SelectItem>
                <SelectItem value="dead">Dead</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="new">Never clicked</SelectItem>
                <SelectItem value="unpublished">Unpublished</SelectItem>
              </SelectContent>
            </Select>
            <Select value={activitySort} onValueChange={setActivitySort}>
              <SelectTrigger className="h-8 w-40 border-[#333] bg-black text-xs text-gray-300" data-testid="activity-sort-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-[#222] bg-[#111]">
                <SelectItem value="needs-attention">Needs attention</SelectItem>
                <SelectItem value="recent-clicks">Recently clicked</SelectItem>
                <SelectItem value="longest-streak">Longest streak</SelectItem>
                <SelectItem value="newest">Newest stories</SelectItem>
              </SelectContent>
            </Select>
            <div className="ml-auto flex flex-wrap gap-2 text-[10px] text-gray-500" data-testid="activity-summary">
              <span data-testid="dead-post-count"><strong className="text-yellow-400">{activityCounts.dead || 0}</strong> dead</span>
              <span data-testid="active-post-count"><strong className="text-green-400">{activityCounts.active || 0}</strong> active</span>
              <span data-testid="new-post-count"><strong className="text-cyan-300">{activityCounts.new || 0}</strong> never clicked</span>
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex items-center gap-1 mb-4 overflow-x-auto pb-2 scrollbar-hide" data-testid="category-tabs">
          {TABS.map(tab => {
            const count = tab === 'All' ? articles.length : articles.filter(a => a.category === tab).length;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`shrink-0 px-3 py-2 text-xs font-medium transition-colors border-b-2 ${
                  activeTab === tab
                    ? 'text-yellow-500 border-yellow-500 bg-yellow-500/10'
                    : 'text-gray-500 border-transparent hover:text-white hover:bg-[#222]'
                }`}
                data-testid={`tab-${tab.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {tab === 'Check Your Claims' ? 'Claims' : tab}
                <span className="ml-1 text-[10px] opacity-60">({count})</span>
              </button>
            );
          })}
        </div>

        {/* Quick select by view count */}
        <div className="flex items-center gap-2 mb-4 p-3 bg-[#0a0a0a] border border-[#222]" data-testid="view-threshold-selector">
          <Eye size={14} className="text-gray-500" />
          <span className="text-xs text-gray-500">Select stories with</span>
          <input
            type="number"
            min="0"
            value={viewThreshold}
            onChange={(e) => setViewThreshold(e.target.value)}
            className="w-16 px-2 py-1 bg-black border border-[#333] text-white text-xs text-center focus:outline-none focus:border-yellow-500"
            data-testid="view-threshold-input"
          />
          <span className="text-xs text-gray-500">views or less</span>
          <button
            onClick={selectByViewThreshold}
            className="px-3 py-1.5 text-xs font-medium bg-[#222] text-gray-300 hover:bg-yellow-500/20 hover:text-yellow-400 transition-colors"
            data-testid="select-by-views-btn"
          >
            Select
          </button>
        </div>

        {/* Security: remove malicious injected ad scripts from article content */}
        <div className="flex items-center gap-2 mb-4 p-3 bg-[#1a0a0a] border border-yellow-900/40" data-testid="clean-scripts-bar">
          <ShieldAlert size={14} className="text-yellow-500" />
          <span className="text-xs text-gray-400 flex-1">
            Scan all stories and remove known malicious/scam redirect scripts (e.g. untimely-hello.com) from their content.
          </span>
          <button
            onClick={handleCleanMaliciousScripts}
            disabled={cleaningScripts}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-yellow-600 text-white hover:bg-yellow-500 transition-colors disabled:opacity-50"
            data-testid="clean-malicious-scripts-btn"
          >
            {cleaningScripts ? <Loader2 size={13} className="animate-spin" /> : <ShieldAlert size={13} />}
            Clean Malicious Scripts
          </button>
        </div>

        {/* Bulk Action Bar */}
        {selected.size > 0 && (          <div className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-[#111] border border-yellow-500/30 animate-fade-in-up" data-testid="bulk-action-bar">
            <span className="text-sm text-white font-medium">{selected.size} selected</span>
            <div className="h-4 w-px bg-[#333]" />
            <button
              onClick={() => { setBulkAction('delete'); setBulkValue(''); }}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-yellow-400 hover:text-yellow-300 bg-yellow-500/10 hover:bg-yellow-500/20 transition-colors"
              data-testid="bulk-delete-btn"
            >
              <Trash2 size={13} /> Delete
            </button>
            <Select onValueChange={(val) => { setBulkAction('category'); setBulkValue(val); }}>
              <SelectTrigger className="w-36 h-8 text-xs bg-black border-[#333] text-gray-300" data-testid="bulk-category-select">
                <SelectValue placeholder="Move to..." />
              </SelectTrigger>
              <SelectContent className="bg-[#111] border-[#222]">
                {['Check On Yo Peoples News Feed', 'Cincy Worldstar', 'Check Your Claims'].map(cat => (
                  <SelectItem key={cat} value={cat} className="text-xs">{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select onValueChange={(val) => { setBulkAction('status'); setBulkValue(val); }}>
              <SelectTrigger className="w-32 h-8 text-xs bg-black border-[#333] text-gray-300" data-testid="bulk-status-select">
                <SelectValue placeholder="Set status..." />
              </SelectTrigger>
              <SelectContent className="bg-[#111] border-[#222]">
                <SelectItem value="published" className="text-xs">Published</SelectItem>
                <SelectItem value="draft" className="text-xs">Draft</SelectItem>
              </SelectContent>
            </Select>
            <button
              onClick={() => { setSelected(new Set()); }}
              className="ml-auto text-xs text-gray-500 hover:text-white transition-colors"
              data-testid="clear-selection-btn"
            >
              Clear
            </button>
          </div>
        )}

        {/* Articles List */}
        <div className="bg-[#111] border border-[#222]">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
            </div>
          ) : filtered.length > 0 ? (
            <div className="divide-y divide-[#222]">
              {/* Select All Header */}
              <div className="flex items-center gap-3 px-4 py-2 bg-[#0a0a0a]">
                <button onClick={toggleAll} className="text-gray-400 hover:text-white transition-colors" data-testid="select-all-btn">
                  {allSelected ? <CheckSquare size={18} className="text-yellow-500" /> : someSelected ? <Minus size={18} className="text-yellow-500" /> : <Square size={18} />}
                </button>
                <span className="text-[10px] text-gray-600 uppercase tracking-wider">
                  {allSelected ? 'Deselect all' : 'Select all'}
                </span>
              </div>

              {filtered.map((article) => (
                <div key={article.id} className={`flex items-start gap-3 p-4 transition-colors ${selected.has(article.id) ? 'bg-yellow-500/5' : 'hover:bg-[#0a0a0a]'}`}>
                  {/* Checkbox */}
                  <button
                    onClick={() => toggleSelect(article.id)}
                    className="mt-0.5 shrink-0 text-gray-400 hover:text-white transition-colors"
                    data-testid={`select-${article.id}`}
                  >
                    {selected.has(article.id) ? <CheckSquare size={18} className="text-yellow-500" /> : <Square size={18} />}
                  </button>

                  <div className="flex-1 min-w-0">
                    {/* Title + Status */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border shrink-0 ${
                            article.post_type === 'video'
                              ? 'border-yellow-500/40 bg-yellow-500/15 text-yellow-300'
                              : 'border-[#333] bg-black text-gray-400'
                          }`}
                          data-testid={`post-type-badge-${article.id}`}
                        >
                          {article.post_type === 'video' ? <PlayCircle size={10} /> : <FileText size={10} />}
                          {article.post_type === 'video' ? 'Video' : 'Article'}
                        </span>
                        <h3 className="font-medium text-white text-sm flex-1 min-w-0 truncate">{article.title || 'Untitled'}</h3>
                      </div>
                      <span className={`shrink-0 px-2 py-0.5 text-[10px] font-medium ${
                        article.status === 'published'
                          ? 'bg-green-500/20 text-green-500'
                          : article.status === 'scheduled'
                          ? 'bg-blue-500/20 text-blue-400'
                          : 'bg-yellow-500/20 text-yellow-500'
                      }`} data-testid={`status-badge-${article.id}`}>
                        {article.status}
                      </span>
                    </div>

                    {/* Meta */}
                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-600">
                      <span className="px-1.5 py-0.5 bg-[#222] text-gray-400 text-[10px]">{article.category}</span>
                      <span className="text-green-500 font-medium">{article.views || 0} views</span>
                      {article.status === 'scheduled' && article.scheduled_at && (
                        <span className="text-blue-400 font-medium" data-testid={`scheduled-time-${article.id}`}>
                          Posts {new Date(article.scheduled_at).toLocaleString()}
                        </span>
                      )}
                    </div>

                    <ArticleActivityTrail article={article} onOpenTrail={handleOpenTrail} />

                    {/* Actions */}
                    <div className="flex items-center gap-1 mt-2">
                      {article.slug && (
                        <a
                          href={`/article/${article.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 px-2 py-1 text-xs text-gray-400 hover:text-green-500 hover:bg-[#222] rounded transition-colors"
                          data-testid={`view-btn-${article.id}`}
                        >
                          <Eye size={14} />
                          <span className="hidden sm:inline">View</span>
                        </a>
                      )}
                      <button
                        onClick={() => {
                          const shareUrl = `${window.location.origin}/api/og/article/${article.slug}`;
                          navigator.clipboard.writeText(shareUrl);
                          toast.success('Share link copied!');
                        }}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-gray-400 hover:text-blue-500 hover:bg-[#222] rounded transition-colors"
                        data-testid={`share-btn-${article.id}`}
                      >
                        <Share2 size={14} />
                        <span className="hidden sm:inline">Share</span>
                      </button>
                      <Link
                        to={`/admin/articles/edit/${article.id}`}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-gray-400 hover:text-white hover:bg-[#222] rounded transition-colors"
                        data-testid={`edit-btn-${article.id}`}
                      >
                        <Edit size={14} />
                        <span className="hidden sm:inline">Edit</span>
                      </Link>
                      <button
                        onClick={() => handleDuplicate(article.id)}
                        disabled={duplicatingId === article.id}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-gray-400 hover:text-yellow-500 hover:bg-[#222] rounded transition-colors disabled:opacity-50"
                        data-testid={`duplicate-btn-${article.id}`}
                      >
                        {duplicatingId === article.id ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Copy size={14} />
                        )}
                        <span className="hidden sm:inline">Duplicate</span>
                      </button>
                      <button
                        onClick={() => setReviveId(article.id)}
                        disabled={revivingId === article.id}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-gray-400 hover:text-green-400 hover:bg-[#222] rounded transition-colors disabled:opacity-50"
                        data-testid={`revive-btn-${article.id}`}
                      >
                        {revivingId === article.id ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                        <span className="hidden sm:inline">Revive</span>
                      </button>
                      <button
                        onClick={() => setDeleteId(article.id)}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-gray-400 hover:text-yellow-500 hover:bg-[#222] rounded transition-colors"
                        data-testid={`delete-btn-${article.id}`}
                      >
                        <Trash2 size={14} />
                        <span className="hidden sm:inline">Delete</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center">
              <Newspaper size={48} className="mx-auto text-gray-700 mb-4" />
              <h3 className="text-lg font-bold text-white">
                {activeTab === 'All' ? 'No stories yet' : `No ${activeTab} stories`}
              </h3>
              <Link
                to="/admin/articles/new"
                className="inline-flex items-center gap-2 mt-4 px-6 py-3 bg-yellow-500 text-white font-medium hover:bg-yellow-600"
              >
                <Plus size={18} />
                Add Story
              </Link>
            </div>
          )}
        </div>

        {/* Single Delete Dialog */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent className="bg-[#111] border-[#222]">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Delete Story</AlertDialogTitle>
              <AlertDialogDescription className="text-gray-500">
                This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-transparent border-[#222] text-gray-400 hover:text-white hover:bg-[#222]">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-yellow-500 hover:bg-yellow-600 text-white" disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={!!reviveId} onOpenChange={(open) => { if (!open && !revivingId) setReviveId(null); }}>
          <AlertDialogContent className="border-[#222] bg-[#111]">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Revive this story?</AlertDialogTitle>
              <AlertDialogDescription className="text-gray-400">
                This keeps the same URL and content, sets the publish time to now, and moves the story back into homepage feeds, RSS, and current-day lists.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-[#333] bg-transparent text-gray-300 hover:bg-[#222] hover:text-white" data-testid="revive-cancel-button">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={(event) => { event.preventDefault(); handleRevive(); }} disabled={!!revivingId} className="bg-green-600 text-white hover:bg-green-500" data-testid="revive-confirm-button">
                {revivingId ? 'Reviving...' : 'Revive Now'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={!!trailArticle} onOpenChange={(open) => { if (!open) { setTrailArticle(null); setTrailData(null); } }}>
          <AlertDialogContent className="max-w-lg border-[#222] bg-[#111]" data-testid="click-trail-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="pr-4 text-white">30-Day Click Trail</AlertDialogTitle>
              <AlertDialogDescription className="line-clamp-2 text-gray-400">{trailArticle?.title}</AlertDialogDescription>
            </AlertDialogHeader>
            {trailLoading ? (
              <div className="flex justify-center py-10" data-testid="click-trail-loading"><Loader2 className="animate-spin text-yellow-500" /></div>
            ) : trailData ? (
              <div className="space-y-3" data-testid="click-trail-history">
                <div className="grid grid-cols-3 gap-2 text-center text-xs">
                  <div className="border border-[#222] bg-black p-2"><strong className="block text-white">{trailData.summary.streak_days}</strong><span className="text-gray-500">day streak</span></div>
                  <div className="border border-[#222] bg-black p-2"><strong className="block text-white">{trailData.summary.days_idle ?? 0}</strong><span className="text-gray-500">days idle</span></div>
                  <div className="border border-[#222] bg-black p-2"><strong className="block text-white">{trailData.summary.tracked_clicks}</strong><span className="text-gray-500">tracked clicks</span></div>
                </div>
                <div className="max-h-64 divide-y divide-[#222] overflow-y-auto border border-[#222]">
                  {[...trailData.history].reverse().map((day) => (
                    <div key={day.date} className="flex items-center justify-between px-3 py-2 text-xs" data-testid={`click-history-day-${day.date}`}>
                      <span className="text-gray-400">{new Date(`${day.date}T12:00:00`).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                      <span className={day.clicks > 0 ? 'font-bold text-green-400' : 'text-gray-600'}>{day.clicks} clicks</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            <AlertDialogFooter>
              <AlertDialogCancel className="border-[#333] bg-transparent text-gray-300 hover:bg-[#222] hover:text-white" data-testid="click-trail-close-button">Close</AlertDialogCancel>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Bulk Action Confirm Dialog */}
        <AlertDialog open={!!bulkAction} onOpenChange={(open) => { if (!open && !bulkProcessing) { setBulkAction(null); setBulkValue(''); } }}>
          <AlertDialogContent className="bg-[#111] border-[#222]">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">
                {bulkAction === 'delete' ? `Delete ${selected.size} stories?` :
                 bulkAction === 'category' ? `Move ${selected.size} stories to "${bulkValue}"?` :
                 `Set ${selected.size} stories to "${bulkValue}"?`}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-gray-500">
                {bulkAction === 'delete' ? 'This cannot be undone.' : 'This will update all selected stories.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-transparent border-[#222] text-gray-400 hover:text-white hover:bg-[#222]">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleBulkAction}
                className={bulkAction === 'delete' ? 'bg-yellow-500 hover:bg-yellow-600 text-white' : 'bg-yellow-500 hover:bg-yellow-600 text-white'}
                disabled={bulkProcessing}
                data-testid="bulk-confirm-btn"
              >
                {bulkProcessing ? 'Processing...' : 'Confirm'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminArticles;
